import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, ArrowLeft, ShieldCheck, Download } from "lucide-react";
import { authApi, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const STATUSES = ["all", "Draft", "Under review", "Further information requested", "Approved", "Rejected", "Access revoked", "Expired"];
const tone = {
  "Approved": "text-signal-green bg-signal-green/12 border-signal-green/30",
  "Under review": "text-signal-amber bg-signal-amber/12 border-signal-amber/30",
  "Further information requested": "text-signal-amber bg-signal-amber/12 border-signal-amber/30",
  "Rejected": "text-signal-red bg-signal-red/12 border-signal-red/30",
  "Access revoked": "text-signal-red bg-signal-red/12 border-signal-red/30",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [legalBasis, setLegalBasis] = useState("");
  const [expiryDays, setExpiryDays] = useState("90");

  useEffect(() => { document.title = "Administrator | norse3"; }, []);
  const load = async () => {
    setLoading(true);
    try { const r = await authApi.get(`/admin/applications?status=${status}`); setApps(r.data); }
    catch (e) { toast.error("Load failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const decide = async (decision) => {
    try {
      const r = await authApi.post(`/admin/applications/${open.id}/decision`, { decision, note, legal_basis: legalBasis, expiry_days: Number(expiryDays) });
      setOpen(r.data); setNote(""); toast.success(`Decision recorded: ${decision}`); load();
    } catch (e) { toast.error("Action failed"); }
  };

  const exportAudit = async () => {
    try {
      const r = await authApi.get("/admin/audit");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "norse3-audit-log.json"; a.click();
    } catch (e) { toast.error("Export failed"); }
  };

  const doLogout = async () => { await logout(); navigate("/investor"); };

  return (
    <div className="min-h-screen pt-16 bg-navy-950">
      <div className="mx-auto max-w-6xl px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-primary">Administrator</p>
            <h1 className="mt-1 font-heading text-2xl font-bold text-foreground">Investor applications</h1>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><a href="/admin/editor">Site editor</a></Button>
            <Button variant="outline" onClick={exportAudit} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Download className="mr-1.5 h-4 w-4" /> Export audit</Button>
            <Button variant="outline" onClick={doLogout} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
          </div>
        </div>

        {!open && (
          <>
            <div className="mt-6 flex items-center gap-2">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-64 bg-navy-800 border-white/12"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/12">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="mt-4 rounded-lg border border-white/10 bg-navy-900/60 overflow-x-auto">
              {loading ? <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 text-primary animate-spin" /></div> : apps.length === 0 ? <p className="p-12 text-center text-sm text-muted-foreground">No applications yet.</p> : (
                <table className="w-full min-w-[640px] border-collapse">
                  <thead><tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Applicant</th><th className="px-4 py-3 font-medium">Category</th><th className="px-4 py-3 font-medium">Capacity</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" /></tr></thead>
                  <tbody>
                    {apps.map((a) => (
                      <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setOpen(a)}>
                        <td className="px-4 py-3 text-sm text-foreground">{a.user_name || a.user_email}<div className="text-xs text-muted-foreground">{a.user_email}</div></td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{(a.classification || {}).category || "—"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{(a.capacity || {}).band || "—"}</td>
                        <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone[a.status] || "text-muted-foreground bg-white/5 border-white/15"}`}>{a.status}</span></td>
                        <td className="px-4 py-3 text-right text-xs text-primary">Review</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {open && (
          <div className="mt-6">
            <button onClick={() => setOpen(null)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to list</button>
            <div className="mt-4 grid lg:grid-cols-2 gap-4">
              <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <h2 className="text-lg font-semibold text-foreground">{open.user_name || open.user_email}</h2>
                <p className="text-sm text-muted-foreground">{open.user_email}</p>
                <span className={`mt-2 inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone[open.status] || "text-muted-foreground bg-white/5 border-white/15"}`}>{open.status}</span>
                <Detail label="Account" obj={open.account} />
                <Detail label="Classification" obj={open.classification} />
                <Detail label="Capacity" obj={open.capacity} />
                {open.signature && <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Signature</p><p className="mt-1 text-sm text-foreground">{open.signature.full_name} · {new Date(open.signature.date).toLocaleString("en-GB")}</p><p className="text-xs text-muted-foreground font-mono break-all">Hash: {open.signature.hash}</p><p className="text-xs text-muted-foreground">IP: {open.signature.ip} · Versions: {JSON.stringify(open.signature.template_versions)}</p></div>}
              </div>

              <div className="rounded-lg border border-primary/25 bg-primary/[0.05] p-6">
                <p className="text-sm font-semibold text-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Decision</p>
                <div className="mt-3 space-y-3">
                  <div><label className="text-xs text-muted-foreground">Legal / exemption basis relied upon</label><Input value={legalBasis} onChange={(e) => setLegalBasis(e.target.value)} className="mt-1 bg-navy-800 border-white/12" placeholder="Record the accepted basis" /></div>
                  <div><label className="text-xs text-muted-foreground">Access expiry (days)</label><Input value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} className="mt-1 bg-navy-800 border-white/12 w-32" /></div>
                  <div><label className="text-xs text-muted-foreground">Internal note</label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-navy-800 border-white/12 resize-none" /></div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => decide("approve")} className="bg-signal-green/20 text-signal-green border border-signal-green/40 hover:bg-signal-green/30">Approve</Button>
                  <Button size="sm" onClick={() => decide("reject")} className="bg-signal-red/20 text-signal-red border border-signal-red/40 hover:bg-signal-red/30">Reject</Button>
                  <Button size="sm" variant="outline" onClick={() => decide("request_info")} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">Request info</Button>
                  <Button size="sm" variant="outline" onClick={() => decide("revoke")} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">Revoke</Button>
                </div>
                {open.notes && open.notes.length > 0 && <div className="mt-4 border-t border-white/10 pt-3 space-y-2">{open.notes.map((n, i) => <p key={i} className="text-xs text-muted-foreground"><span className="text-foreground">{n.by}:</span> {n.text}</p>)}</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, obj }) {
  if (!obj || Object.keys(obj).length === 0) return null;
  return (
    <div className="mt-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {Object.entries(obj).map(([k, v]) => (
          <React.Fragment key={k}><dt className="text-muted-foreground">{k}</dt><dd className="text-foreground break-words">{String(v)}</dd></React.Fragment>
        ))}
      </dl>
    </div>
  );
}
